import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/apiError';
import {
  Wand2, Loader2, CheckCircle2, XCircle, Circle, Save, PlayCircle, ExternalLink, Truck, Bell, DollarSign, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICON = {
  shipping: Truck,
  notifications: Bell,
  billing: DollarSign,
  ai: Sparkles,
};

const CATEGORY_LABEL = {
  shipping: 'Shipping companies',
  notifications: 'Notifications',
  billing: 'Billing',
  ai: 'AI',
};

const STATUS_STYLE = {
  green:   { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', label: 'Connected' },
  red:     { icon: XCircle,      cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',           label: 'Failing' },
  skipped: { icon: Circle,       cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',         label: 'Not tested' },
};

export default function AdminSetupWizardPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [state, setState] = useState(null);   // { steps, summary }
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});   // { serviceId: { fieldKey: val } }
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/wizard/status`, { headers: auth });
      if (!r.ok) throw new Error('Admin only');
      const d = await r.json();
      setState(d);
    } catch (e) {
      toast.error(e.message);
      setState({ steps: [], summary: {} });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setDraft = (svcId, key, val) => {
    setDrafts((prev) => ({ ...prev, [svcId]: { ...(prev[svcId] || {}), [key]: val } }));
  };

  const save = async (svc) => {
    setBusy((b) => ({ ...b, [`save:${svc.id}`]: true }));
    try {
      const values = drafts[svc.id] || {};
      // Only send non-empty overrides — leave secrets untouched if user didn't retype
      const clean = Object.fromEntries(Object.entries(values).filter(([, v]) => v && String(v).trim()));
      if (!Object.keys(clean).length) throw new Error('Nothing to save. Enter at least one field.');
      const r = await fetch(`${API}/admin/wizard/save/${svc.id}`, {
        method: 'POST', headers: auth, body: JSON.stringify({ values: clean }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(apiErrorMessage(j, 'Save failed'));
      toast.success(j.message || 'Saved');
      setDrafts((prev) => ({ ...prev, [svc.id]: {} }));
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy((b) => ({ ...b, [`save:${svc.id}`]: false })); }
  };

  const test = async (svc) => {
    setBusy((b) => ({ ...b, [`test:${svc.id}`]: true }));
    try {
      const r = await fetch(`${API}/admin/wizard/test/${svc.id}`, { method: 'POST', headers: auth });
      const j = await r.json();
      if (j.status === 'green') toast.success(`${svc.name}: ${j.detail || 'OK'}`);
      else if (j.status === 'red') toast.error(`${svc.name}: ${j.detail || 'failed'}`);
      else toast(`${svc.name}: ${j.detail || 'not testable'}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy((b) => ({ ...b, [`test:${svc.id}`]: false })); }
  };

  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div>;
  }

  const byCategory = {};
  (state?.steps || []).forEach((s) => { (byCategory[s.category] = byCategory[s.category] || []).push(s); });

  const summary = state?.summary || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-wizard-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Wand2 className="w-6 h-6 text-indigo-500" /> Admin Setup Wizard
        </h1>
        <p className="text-slate-500 mt-1">One place to connect every shipping company and support service. Save keys, then hit “Test” to verify.</p>
      </div>

      {/* Summary strip */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-sm font-medium" data-testid="summary-green">
            <CheckCircle2 className="w-4 h-4" /> {summary.green || 0} connected
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 text-sm font-medium" data-testid="summary-red">
            <XCircle className="w-4 h-4" /> {summary.red || 0} failing
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 text-sm font-medium" data-testid="summary-skipped">
            <Circle className="w-4 h-4" /> {summary.skipped || 0} not tested
          </div>
          <div className="flex-1"></div>
          <div className="text-sm text-slate-500 self-center">{summary.configured || 0} of {summary.total || 0} services have credentials</div>
        </CardContent>
      </Card>

      {Object.entries(byCategory).map(([cat, items]) => {
        const Icon = CATEGORY_ICON[cat] || Truck;
        return (
          <div key={cat} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-2">
              <Icon className="w-4 h-4" /> {CATEGORY_LABEL[cat] || cat}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {items.map((svc) => {
                const st = STATUS_STYLE[svc.status] || STATUS_STYLE.skipped;
                const StIcon = st.icon;
                return (
                  <Card key={svc.id} className="border-slate-200 dark:border-slate-800" data-testid={`svc-${svc.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            {svc.name}
                            {svc.configured && <span className="text-[10px] font-semibold uppercase text-slate-400">saved</span>}
                          </CardTitle>
                          <CardDescription className="mt-1 text-xs">{svc.description}</CardDescription>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${st.cls}`} data-testid={`svc-${svc.id}-status`}>
                          <StIcon className="w-3.5 h-3.5" /> {st.label}
                        </span>
                      </div>
                      {svc.docs_url && (
                        <a href={svc.docs_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-500 hover:underline inline-flex items-center gap-1 mt-1">
                          Get credentials <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {svc.fields.map((f) => {
                        const draftVal = (drafts[svc.id] || {})[f.key] ?? '';
                        return (
                          <div key={f.key} className="space-y-1">
                            <Label className="text-xs">{f.label}{f.secret ? ' *' : ''}</Label>
                            <Input
                              type={f.secret ? 'password' : 'text'}
                              value={draftVal}
                              onChange={(e) => setDraft(svc.id, f.key, e.target.value)}
                              placeholder={f.current_masked ? `Current: ${f.current_masked}` : 'Not set'}
                              autoComplete="off"
                              data-testid={`field-${svc.id}-${f.key}`}
                            />
                          </div>
                        );
                      })}
                      {svc.detail && (
                        <p className="text-[11px] text-slate-500 truncate" data-testid={`svc-${svc.id}-detail`} title={svc.detail}>
                          Last probe: {svc.detail}
                        </p>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => save(svc)}
                          disabled={busy[`save:${svc.id}`]}
                          data-testid={`save-${svc.id}`}
                        >
                          {busy[`save:${svc.id}`] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => test(svc)}
                          disabled={busy[`test:${svc.id}`] || !svc.configured}
                          data-testid={`test-${svc.id}`}
                        >
                          {busy[`test:${svc.id}`] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                          Test
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
